import { useState, useEffect, useMemo } from "react";
import { FileText, Download, Filter, Search, ChevronDown, RefreshCw, Hash } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { type RelatorioParticipante } from "@/lib/fakeApi";
import { queueApiService } from "@/services/queueApi.service";

interface QueueOption {
  id: string;
  name: string;
}

interface RepeatGroupField {
  fieldKey: string;
  label: string;
}

interface RepeatGroupConfig {
  groupKey: string;
  groupLabel: string;
  fields: RepeatGroupField[];
}

interface FullCustomFieldConfig {
  fieldKey: string;
  fieldType: string;
  label: string;
  repeatGroup?: RepeatGroupConfig;
}

interface DynamicColumn {
  key: string;
  label: string;
  type: "simple" | "subgroup";
  subFields?: RepeatGroupField[];
}

export default function EventsRelatorios() {
  const { toast } = useToast();

  const [participantes, setParticipantes] = useState<RelatorioParticipante[]>([]);
  const [filteredParticipantes, setFilteredParticipantes] = useState<RelatorioParticipante[]>([]);
  const [rawConfig, setRawConfig] = useState<FullCustomFieldConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [queueFilter, setQueueFilter] = useState<string>("todas");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isQueueDropdownOpen, setIsQueueDropdownOpen] = useState(false);
  const [queues, setQueues] = useState<QueueOption[]>([]);

  useEffect(() => {
    loadQueues();
    loadParticipantes();
  }, []);

  useEffect(() => {
    applySearchFilter();
  }, [participantes, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isQueueDropdownOpen && !target.closest(".queue-dropdown-container")) {
        setIsQueueDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isQueueDropdownOpen]);

  // Build dynamic columns from config + actual data
  const dynamicColumns = useMemo(() => {
    const configByKey = new Map<string, FullCustomFieldConfig>();
    const groupKeyToConfig = new Map<string, FullCustomFieldConfig>();
    for (const cfg of rawConfig) {
      configByKey.set(cfg.fieldKey, cfg);
      if (cfg.repeatGroup) {
        groupKeyToConfig.set(cfg.repeatGroup.groupKey, cfg);
      }
    }

    // Collect all unique keys from participant customFields
    const allKeys = new Set<string>();
    for (const p of participantes) {
      if (p.customFields) {
        Object.keys(p.customFields).forEach((k) => allKeys.add(k));
      }
    }

    const columns: DynamicColumn[] = [];
    const processedKeys = new Set<string>();

    // First, process keys that are arrays (subgroups) — only used in CSV export
    for (const key of allKeys) {
      const hasArray = participantes.some(
        (p) => Array.isArray(p.customFields[key]),
      );
      if (!hasArray) continue;

      const groupConfig = groupKeyToConfig.get(key);
      if (groupConfig?.repeatGroup) {
        columns.push({
          key,
          label: groupConfig.label || groupConfig.repeatGroup.groupLabel || key,
          type: "subgroup",
          subFields: groupConfig.repeatGroup.fields,
        });
      } else {
        // Infer subFields from actual data
        const subFieldKeys = new Set<string>();
        for (const p of participantes) {
          const arr = p.customFields[key];
          if (Array.isArray(arr)) {
            arr.forEach((item: any) => {
              if (item && typeof item === "object") {
                Object.keys(item).forEach((k) => subFieldKeys.add(k));
              }
            });
          }
        }
        columns.push({
          key,
          label: key,
          type: "subgroup",
          subFields: Array.from(subFieldKeys).map((k) => ({
            fieldKey: k,
            label: k,
          })),
        });
      }
      processedKeys.add(key);
    }

    // Then simple fields
    for (const key of allKeys) {
      if (processedKeys.has(key)) continue;
      const cfg = configByKey.get(key);
      columns.push({
        key,
        label: cfg?.label || key,
        type: "simple",
      });
      processedKeys.add(key);
    }

    return columns;
  }, [participantes, rawConfig]);

  const simpleColumns = dynamicColumns.filter((c) => c.type === "simple");
  const subgroupColumns = dynamicColumns.filter((c) => c.type === "subgroup");

  // Identify numeric custom fields and compute sums split by status groups
  const droppedStatuses = new Set(["abandoned", "absent", "cancelled"]);

  const numericFieldSums = useMemo(() => {
    const numericConfigs = rawConfig.filter((cfg) => cfg.fieldType === "number");
    if (numericConfigs.length === 0) return [];

    return numericConfigs.map((cfg) => {
      let sumCompleted = 0;
      let sumWaiting = 0;
      let sumDropped = 0;

      for (const p of filteredParticipantes) {
        const raw = p.customFields?.[cfg.fieldKey];
        const val = Number(raw);
        if (isNaN(val)) continue;

        if (p.status === "completed") {
          sumCompleted += val;
        } else if (droppedStatuses.has(p.status)) {
          sumDropped += val;
        } else {
          sumWaiting += val;
        }
      }

      return {
        label: cfg.label || cfg.fieldKey,
        sumCompleted,
        sumWaiting,
        sumDropped,
      };
    });
  }, [filteredParticipantes, rawConfig]);

  const loadQueues = async () => {
    try {
      const data = await queueApiService.listQueues();
      setQueues(data.map((q: any) => ({ id: q._id || q.id, name: q.name })));
    } catch {
      // Queues will just not be available for filtering
    }
  };

  const loadParticipantes = async (filters?: {
    queueId?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    try {
      setIsLoading(true);
      const result = await queueApiService.getRelatorioParticipantes(filters);
      setParticipantes(result.participants);
      setRawConfig(result.customFieldsConfig as any);
    } catch {
      toast({
        title: "Erro",
        description: "Não foi possível carregar os relatórios",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const applySearchFilter = () => {
    let filtered = [...participantes];
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.nome.toLowerCase().includes(term) ||
          p.telefone.includes(term) ||
          p.email.toLowerCase().includes(term) ||
          p.cpf.includes(term),
      );
    }
    setFilteredParticipantes(filtered);
  };

  const handleApplyFilters = () => {
    const filters: { queueId?: string; startDate?: string; endDate?: string } = {};
    if (queueFilter !== "todas") filters.queueId = queueFilter;
    if (startDate) filters.startDate = new Date(startDate).toISOString();
    if (endDate) filters.endDate = new Date(endDate).toISOString();
    loadParticipantes(filters);
  };

  const handleClearFilters = () => {
    setQueueFilter("todas");
    setStartDate("");
    setEndDate("");
    setSearchTerm("");
    loadParticipantes();
  };

  const formatSimpleValue = (value: unknown): string => {
    if (value === null || value === undefined) return "";
    if (typeof value === "boolean") return value ? "Sim" : "Não";
    return String(value);
  };

  const escapeCsv = (value: string): string => {
    if (value.includes(",") || value.includes('"') || value.includes("\n")) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  const handleExportCSV = () => {
    const baseHeaders = ["Nome", "Telefone", "Email", "CPF", "Mecânica", "Status", "Posição", "Cadastro"];
    const simpleHeaders = simpleColumns.map((c) => c.label);

    // Build subgroup headers: find max repetitions for each subgroup, create flat columns
    const subgroupMeta = subgroupColumns.map((col) => {
      let maxCount = 0;
      for (const p of filteredParticipantes) {
        const arr = p.customFields[col.key];
        if (Array.isArray(arr) && arr.length > maxCount) maxCount = arr.length;
      }
      const fields = col.subFields || [];
      const headers: string[] = [];
      for (let i = 0; i < maxCount; i++) {
        for (const sf of fields) {
          headers.push(`${col.label} ${i + 1} - ${sf.label}`);
        }
      }
      return { col, maxCount, fields, headers };
    });

    const allHeaders = [
      ...baseHeaders,
      ...simpleHeaders,
      ...subgroupMeta.flatMap((m) => m.headers),
    ];

    const csvContent = [
      allHeaders.map((h) => escapeCsv(h)).join(","),
      ...filteredParticipantes.map((p) => {
        const baseValues = [
          escapeCsv(p.nome),
          escapeCsv(p.telefone),
          escapeCsv(p.email),
          escapeCsv(p.cpf),
          escapeCsv(p.mecanica),
          escapeCsv(formatStatus(p.status).label),
          String(p.posicao),
          escapeCsv(formatDate(p.cadastro)),
        ];

        const simpleValues = simpleColumns.map((c) =>
          escapeCsv(formatSimpleValue(p.customFields[c.key])),
        );

        const subgroupValues = subgroupMeta.flatMap((meta) => {
          const arr = p.customFields[meta.col.key];
          const values: string[] = [];
          for (let i = 0; i < meta.maxCount; i++) {
            const item = Array.isArray(arr) ? arr[i] : undefined;
            for (const sf of meta.fields) {
              values.push(
                escapeCsv(item ? formatSimpleValue(item[sf.fieldKey]) : ""),
              );
            }
          }
          return values;
        });

        return [...baseValues, ...simpleValues, ...subgroupValues].join(",");
      }),
    ].join("\n");

    const bom = "\uFEFF";
    const blob = new Blob([bom + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `relatorio_participantes_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast({
      title: "Sucesso",
      description: "Relatório exportado com sucesso!",
    });
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString("pt-BR");
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("pt-BR");
  };

  const formatStatus = (status: string) => {
    const map: Record<string, { label: string; className: string }> = {
      waiting: { label: "Aguardando", className: "text-status-attention bg-brand-attention/10" },
      called: { label: "Chamado", className: "text-primary bg-primary/10" },
      serving: { label: "Em atendimento", className: "text-brand-logo-2 bg-brand-logo-2/10" },
      completed: { label: "Concluído", className: "text-status-ok bg-brand-green/10" },
      abandoned: { label: "Abandonou", className: "text-status-danger bg-brand-danger/10" },
      absent: { label: "Ausente", className: "text-status-attention bg-brand-attention/10" },
      cancelled: { label: "Cancelado", className: "text-muted-foreground bg-muted" },
    };
    return map[status] || { label: status, className: "text-muted-foreground bg-muted" };
  };

  const totalCols = 7 + simpleColumns.length; // 7 base columns + simple custom fields

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-64 mb-4"></div>
          <div className="space-y-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-16 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-10 w-10 flex-none items-center justify-center rounded-[var(--radius)] bg-brand-logo-3/10">
            <FileText className="text-brand-logo-3" size={20} />
          </div>
          <div className="flex-1">
            <h1 className="text-[16px] font-bold text-foreground">Relatórios</h1>
            <p className="text-[13px] text-muted-foreground">
              Relatório completo dos participantes cadastrados
            </p>
          </div>
        </div>

        <button
          onClick={handleExportCSV}
          disabled={filteredParticipantes.length === 0}
          className="btn-primary flex items-center gap-2 disabled:opacity-50"
        >
          <Download size={20} />
          Exportar CSV
        </button>
      </div>

      {/* Filters */}
      <div className="bg-card p-6 rounded-xl border border-border">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
                <input
                  type="text"
                  placeholder="Buscar por nome, telefone, email ou CPF..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-full border border-input bg-card py-2 pl-10 pr-4 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                />
              </div>
            </div>

            {/* Queue Filter */}
            <div className="relative queue-dropdown-container">
              <button
                onClick={() => setIsQueueDropdownOpen(!isQueueDropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 bg-muted border border-border rounded-[20px] text-foreground hover:bg-opacity-80 transition-colors min-w-[180px] justify-between"
              >
                <div className="flex items-center gap-2">
                  <Filter size={16} />
                  <span className="text-sm truncate max-w-[120px]">
                    {queueFilter === "todas"
                      ? "Todas as filas"
                      : queues.find((q) => q.id === queueFilter)?.name || "Fila"}
                  </span>
                </div>
                <ChevronDown
                  className={`transition-transform duration-200 flex-shrink-0 ${isQueueDropdownOpen ? "rotate-180" : ""}`}
                  size={16}
                />
              </button>

              {isQueueDropdownOpen && (
                <div className="absolute top-full right-0 mt-1 bg-muted border border-border rounded-xl shadow-lg z-10 min-w-[180px]">
                  <button
                    onClick={() => {
                      setQueueFilter("todas");
                      setIsQueueDropdownOpen(false);
                    }}
                    className="w-full px-4 py-3 text-left text-foreground hover:bg-brand-logo-1/10 transition-colors rounded-t-xl"
                  >
                    Todas as filas
                  </button>
                  {queues.map((queue) => (
                    <button
                      key={queue.id}
                      onClick={() => {
                        setQueueFilter(queue.id);
                        setIsQueueDropdownOpen(false);
                      }}
                      className="w-full px-4 py-3 text-left text-foreground hover:bg-brand-logo-1/10 transition-colors last:rounded-b-xl"
                    >
                      {queue.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Date range + action buttons */}
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex gap-4 flex-1">
              <div className="flex flex-col gap-1">
                <label className="text-xs text-muted-foreground">Data/hora início</label>
                <input
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-3 py-2 bg-muted border border-border rounded-[20px] text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-brand-logo-1"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs text-muted-foreground">Data/hora fim</label>
                <input
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-3 py-2 bg-muted border border-border rounded-[20px] text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-brand-logo-1"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleApplyFilters}
                className="btn-primary flex items-center gap-2 text-sm"
              >
                <RefreshCw size={16} />
                Aplicar filtros
              </button>
              {(queueFilter !== "todas" || startDate || endDate) && (
                <button
                  onClick={handleClearFilters}
                  className="px-4 py-2 bg-muted border border-border rounded-[20px] text-muted-foreground hover:text-foreground text-sm transition-colors"
                >
                  Limpar
                </button>
              )}
            </div>

            {/* Results Count */}
            <div className="flex items-center gap-2 text-muted-foreground text-sm whitespace-nowrap">
              <Filter size={16} />
              <span>
                {filteredParticipantes.length} de {participantes.length} participantes
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Numeric Custom Field Summaries */}
      {numericFieldSums.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {numericFieldSums.map((field) => (
            <div
              key={field.label}
              className="bg-card border border-border rounded-xl p-4 sm:p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-brand-logo-1/10 rounded-full flex items-center justify-center">
                  <Hash className="text-brand-logo-1" size={20} />
                </div>
                <p className="text-sm font-medium text-foreground">{field.label}</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-brand-green/5 rounded-ds-card p-3">
                  <p className="text-xs text-muted-foreground mb-1">Concluídos</p>
                  <p className="text-xl font-bold text-status-ok">
                    {formatNumber(field.sumCompleted)}
                  </p>
                </div>
                <div className="bg-primary/5 rounded-ds-card p-3">
                  <p className="text-xs text-muted-foreground mb-1">Na fila</p>
                  <p className="text-xl font-bold text-primary">
                    {formatNumber(field.sumWaiting)}
                  </p>
                </div>
                <div className="bg-brand-danger/5 rounded-ds-card p-3">
                  <p className="text-xs text-muted-foreground mb-1">Desistentes</p>
                  <p className="text-xl font-bold text-status-danger">
                    {formatNumber(field.sumDropped)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="text-left px-4 pt-4 pb-3 text-[12px] font-semibold text-[var(--text-faint-2)]">Nome</th>
                <th className="text-left px-4 pt-4 pb-3 text-[12px] font-semibold text-[var(--text-faint-2)]">Telefone</th>
                <th className="text-left px-4 pt-4 pb-3 text-[12px] font-semibold text-[var(--text-faint-2)]">Email</th>
                <th className="text-left px-4 pt-4 pb-3 text-[12px] font-semibold text-[var(--text-faint-2)]">CPF</th>
                <th className="text-left px-4 pt-4 pb-3 text-[12px] font-semibold text-[var(--text-faint-2)]">Mecânica</th>
                <th className="text-left px-4 pt-4 pb-3 text-[12px] font-semibold text-[var(--text-faint-2)]">Status</th>
                {simpleColumns.map((col) => (
                  <th key={col.key} className="text-left px-4 pt-4 pb-3 text-[12px] font-semibold text-[var(--text-faint-2)]">
                    {col.label}
                  </th>
                ))}
                <th className="text-left px-4 pt-4 pb-3 text-[12px] font-semibold text-[var(--text-faint-2)]">Cadastro</th>
              </tr>
            </thead>
            <tbody>
              {filteredParticipantes.length === 0 ? (
                <tr>
                  <td
                    colSpan={totalCols}
                    className="text-center py-12 text-muted-foreground"
                  >
                    {searchTerm.trim()
                      ? "Nenhum participante encontrado com os filtros aplicados"
                      : "Nenhum participante cadastrado ainda"}
                  </td>
                </tr>
              ) : (
                filteredParticipantes.map((participante) => {
                  const statusInfo = formatStatus(participante.status);
                  return (
                    <tr
                      key={participante.id}
                      className="border-t border-border hover:bg-[var(--tabela-linha-hover)] transition-colors"
                    >
                      <td className="px-4 py-4 align-middle text-[13px]">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-brand-logo-1/10 rounded-full flex items-center justify-center">
                            <span className="text-brand-logo-1 text-sm font-medium">
                              {participante.nome
                                .split(" ")
                                .map((n) => n[0])
                                .join("")
                                .substring(0, 2)}
                            </span>
                          </div>
                          <span className="font-medium text-foreground">{participante.nome}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 align-middle text-[13px] text-foreground">{participante.telefone}</td>
                      <td className="px-4 py-4 align-middle text-[13px] text-foreground">{participante.email}</td>
                      <td className="px-4 py-4 align-middle text-[13px] text-foreground font-mono">{participante.cpf}</td>
                      <td className="px-4 py-4 align-middle text-[13px]">
                        <span className="px-2 py-1 bg-brand-logo-1/10 text-brand-logo-1 rounded-[20px] text-sm">
                          {participante.mecanica}
                        </span>
                      </td>
                      <td className="px-4 py-4 align-middle text-[13px]">
                        <span className={`px-2 py-1 rounded-[20px] text-sm ${statusInfo.className}`}>
                          {statusInfo.label}
                        </span>
                      </td>
                      {simpleColumns.map((col) => (
                        <td key={col.key} className="px-4 py-4 align-middle text-[13px] text-foreground">
                          {formatSimpleValue(participante.customFields[col.key])}
                        </td>
                      ))}
                      <td className="px-4 py-4 align-middle text-[13px] text-muted-foreground">{formatDate(participante.cadastro)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
